// client/src/components/pages/Geofencing/AerialOverlayManager.tsx (converted from .js, ST-00228)
// Upload + manage surveyor drone orthos (ST-00125, ST-00133). Files go into a
// module-level upload queue (aerialUploadQueue.js) that survives navigation:
// drop one or more GeoTIFFs, switch tabs or pages freely, come back and each
// finished upload is waiting with a "Review & Save" step — satellite alignment
// preview, zone outlines, pre-filled bounds/name/capture date.
//
// ST-00228: the card list asks for inactive overlays too (audit TRK-15: an overlay
// switched to "Inactive" used to vanish for good, so nothing could switch it back on;
// the live map still shows active ones only). Uploading, saving, switching and deleting
// need `geofencing.manage` (audit TRK-10); without it the cards are read-only.
//
// ST-00281: the drop zone and its "Choose files" button are on the page from the start (an
// "Upload" button had to be clicked first to show them), and dropping files on the zone
// works — it used to say "drop here" without handling a drop. Picked and dropped files go
// through the same type and size checks.
import { useState, useEffect, useRef, useMemo } from 'react';
import type { ChangeEvent, ComponentType, DragEvent, FormEvent, SyntheticEvent } from 'react';
import type { AxiosError } from 'axios';
import type { FeatureCollection, Polygon } from 'geojson';
import { Source, Layer } from 'react-map-gl/maplibre';
import type { MapProps } from 'react-map-gl/maplibre';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import LexiStarMapJs from '../../map/LexiStarMap';
import {
  polygonRingFromLatLngs as polygonRingFromLatLngsJs,
  circleToPolygonRing as circleToPolygonRingJs,
} from '../../map/geo';
import { getZoneType as getZoneTypeJs, getZoneTypeMeta as getZoneTypeMetaJs } from './zoneTypes';
import * as uploadQueueJs from './aerialUploadQueue';
import {
  FaImage,
  FaUpload,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaSyncAlt,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaGlobeAfrica,
  FaInfoCircle,
  FaDrawPolygon,
  FaClock,
} from 'react-icons/fa';
import type { LexiStarMapProps } from '../../../types/commonComponents';
import type { GeofenceRow, LatLngPair } from '../../../types/clientPages/geofencing';
import type {
  AerialOverlayRow,
  AerialOverlaysResponse,
  AerialUploadItem,
  AerialUploadQueue,
  AerialUploadQueueState,
  LngLat,
} from '../../../types/clientPages/aerialOverlays';

// Typed views of the JavaScript modules this component uses. The review preview fits
// the map to the overlay's bounds, so initialViewState takes react-map-gl's full shape.
const LexiStarMap = LexiStarMapJs as unknown as ComponentType<
  Omit<LexiStarMapProps, 'initialViewState'> & { initialViewState?: MapProps['initialViewState'] }
>;
const polygonRingFromLatLngs = polygonRingFromLatLngsJs as unknown as (
  coords: LatLngPair[] | null
) => LngLat[] | null;
const circleToPolygonRing = circleToPolygonRingJs as unknown as (
  lng: number | null,
  lat: number | null,
  radiusMeters: number
) => LngLat[] | null;
const getZoneType = getZoneTypeJs as unknown as (zone: GeofenceRow) => string;
const getZoneTypeMeta = getZoneTypeMetaJs as unknown as (id: string) => { color: string };
const uploadQueue = uploadQueueJs as unknown as AerialUploadQueue;

/** The four corners of an overlay image, TL, TR, BR, BL (MapLibre image source order). */
type LngLatQuad = [LngLat, LngLat, LngLat, LngLat];

/** Post-save prompt: the overlay just saved and how many zones predate its capture. */
interface ZoneReviewPrompt {
  name: string;
  staleCount: number;
}

export interface AerialOverlayManagerProps {
  onOverlayChange?: () => void;
  geofences?: GeofenceRow[];
  onReviewZones?: () => void;
}

const errorMessage = (err: unknown, fallback: string): string =>
  (err as AxiosError<{ error?: string }>)?.response?.data?.error || fallback;

/** A thumbnail or preview that fails to load is hidden instead of showing a broken image. */
const hideBrokenImage = (e: SyntheticEvent<HTMLImageElement>): void => {
  e.currentTarget.style.display = 'none';
};

const parseCoordinates = (coords: GeofenceRow['Coordinates']): LatLngPair[] | null => {
  if (!coords) return null;
  return typeof coords === 'string' ? (JSON.parse(coords) as LatLngPair[]) : coords;
};

const serverBase =
  import.meta.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5002';

const ACCEPTED_EXTS = ['.tif', '.tiff', '.png', '.jpg', '.jpeg', '.webp'];
const MAX_UPLOAD_MB = 500;

const fmtGsd = (gsdMeters: number | null | undefined): string | null => {
  const n = Number(gsdMeters);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1 ? `${(n * 100).toFixed(1)} cm/px` : `${n.toFixed(2)} m/px`;
};

const fmtMB = (bytes: number): string =>
  bytes > 0 ? `${(bytes / 1024 / 1024).toFixed(bytes > 10 * 1024 * 1024 ? 0 : 1)} MB` : '';

const nameFromFileName = (fileName: string): string =>
  fileName.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');

export default function AerialOverlayManager({
  onOverlayChange,
  geofences = [],
  onReviewZones,
}: AerialOverlayManagerProps) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('geofencing.manage');
  const [overlays, setOverlays] = useState<AerialOverlayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Files are being dragged over the drop zone (highlights it).
  const [dragging, setDragging] = useState(false);

  // Upload queue (module-level; survives tab/page navigation)
  const [queue, setQueue] = useState<AerialUploadQueueState>({ items: [], uploading: false });
  // Item currently in the Review & Save step
  const [reviewId, setReviewId] = useState<number | null>(null);

  // Review form state
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCaptureDate, setFormCaptureDate] = useState('');
  const [formOpacity, setFormOpacity] = useState(0.8);
  // Bounds — auto-detected or manual
  const [formNorth, setFormNorth] = useState('');
  const [formSouth, setFormSouth] = useState('');
  const [formEast, setFormEast] = useState('');
  const [formWest, setFormWest] = useState('');
  const [boundsEdited, setBoundsEdited] = useState(false);
  // Post-save zone-review prompt: { name, staleCount } of the overlay just saved
  const [zoneReview, setZoneReview] = useState<ZoneReviewPrompt | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => uploadQueue.subscribe(setQueue), []);

  const reviewItem = useMemo(
    () => queue.items.find((i) => i.id === reviewId) || null,
    [queue.items, reviewId]
  );
  const detection = reviewItem?.detection || null;
  const boundsDetected = !!detection?.boundsDetected;

  // If the reviewed item disappears (dismissed elsewhere), drop out of review.
  useEffect(() => {
    if (reviewId !== null && !reviewItem) setReviewId(null);
  }, [reviewId, reviewItem]);

  const fetchOverlays = async () => {
    try {
      setLoading(true);
      setError(null);
      // includeInactive: an overlay switched off stays on the list, so it can be
      // switched back on (TRK-15). The live map asks for active overlays only.
      const res = await api.aerialOverlays.list({ includeInactive: true });
      setOverlays((res.data as AerialOverlaysResponse | undefined)?.data || []);
    } catch {
      setError('Failed to load aerial overlays.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverlays();
  }, []);

  // Files picked or dropped → validate and hand to the queue; uploads run
  // independently of this component's lifecycle.
  const addFiles = (files: File[]) => {
    if (!files.length) return;

    const problems: string[] = [];
    const valid: File[] = [];
    for (const file of files) {
      const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
      if (!ACCEPTED_EXTS.includes(ext)) {
        problems.push(`${file.name}: unsupported type "${ext}" (use GeoTIFF, PNG, JPG or WebP)`);
      } else if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        problems.push(
          `${file.name}: ${(file.size / 1024 / 1024).toFixed(0)}MB exceeds the ${MAX_UPLOAD_MB}MB limit`
        );
      } else {
        valid.push(file);
      }
    }
    setError(problems.length ? problems.join(' · ') : null);
    if (valid.length) uploadQueue.enqueue(valid);
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    addFiles(files);
  };

  // Drag and drop onto the drop zone. Only file drags are taken (dragging text or a
  // link over the zone does nothing).
  const isFileDrag = (e: DragEvent<HTMLElement>): boolean =>
    Array.from(e.dataTransfer?.types || []).includes('Files');
  const handleDragOver = (e: DragEvent<HTMLElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragging) setDragging(true);
  };
  const handleDragLeave = (e: DragEvent<HTMLElement>) => {
    // Leaving for a child of the zone is not leaving the zone.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
  };
  const handleDrop = (e: DragEvent<HTMLElement>) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const openReview = (item: AerialUploadItem) => {
    const d = item.detection;
    setReviewId(item.id);
    setFormName(nameFromFileName(item.fileName));
    setFormDescription('');
    setFormCaptureDate(d?.suggestedCaptureDate || '');
    setFormOpacity(0.8);
    if (d?.boundsDetected && d.bounds) {
      setFormNorth(String(d.bounds.north));
      setFormSouth(String(d.bounds.south));
      setFormEast(String(d.bounds.east));
      setFormWest(String(d.bounds.west));
    } else {
      setFormNorth('');
      setFormSouth('');
      setFormEast('');
      setFormWest('');
    }
    setBoundsEdited(false);
  };

  const setBoundsField = (setter: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setBoundsEdited(true);
  };

  // Review confirmed → save overlay, release the queue item
  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!detection) return;
    const n = parseFloat(formNorth),
      s = parseFloat(formSouth);
    const ea = parseFloat(formEast),
      w = parseFloat(formWest);
    if (!formName.trim() || [n, s, ea, w].some((v) => !Number.isFinite(v))) {
      setError('Please fill in the name and all bounds coordinates.');
      return;
    }
    if (n <= s || ea <= w) {
      setError('North must be greater than south, and east greater than west.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.aerialOverlays.save({
        name: formName.trim(),
        description: formDescription,
        captureDate: formCaptureDate,
        boundsNorth: n,
        boundsSouth: s,
        boundsEast: ea,
        boundsWest: w,
        opacity: formOpacity,
        filePath: detection.filePath,
        thumbPath: detection.thumbPath,
        sourcePath: detection.sourcePath,
        // The reprojected quad is only valid while the bounds it came with
        // are untouched; manual edits fall back to the plain rectangle.
        corners: !boundsEdited && detection.corners ? detection.corners : undefined,
        nativeCrs: detection.nativeCrs || undefined,
        gsdMeters: detection.gsdMeters ?? undefined,
      });

      // Fresh imagery means the pile outlines may no longer match reality —
      // prompt a zone review rather than trusting anyone to remember.
      setZoneReview({ name: formName.trim(), staleCount: countStaleZones(formCaptureDate) });

      uploadQueue.markSaved(reviewId);
      setReviewId(null);
      await fetchOverlays();
      onOverlayChange?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save overlay. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove this aerial overlay?')) return;
    try {
      await api.aerialOverlays.delete(id);
      await fetchOverlays();
      onOverlayChange?.();
    } catch {
      setError('Failed to delete overlay.');
    }
  };

  const handleToggleActive = async (overlay: AerialOverlayRow) => {
    try {
      await api.aerialOverlays.update(overlay.overlay_id, { isActive: !overlay.is_active });
      await fetchOverlays();
      onOverlayChange?.();
    } catch {
      setError('Failed to update overlay.');
    }
  };

  // Existing zones as GeoJSON so the surveyor can check pile outlines against
  // the new imagery in the same glance as the alignment check (same shape
  // logic as LiveTrackingMap's zones source).
  const zonesFC = useMemo((): FeatureCollection<Polygon, { color: string }> => {
    const features: FeatureCollection<Polygon, { color: string }>['features'] = [];
    geofences.forEach((g) => {
      if (!g.is_active) return;
      const ring =
        g.GeofenceType === 'POLYGON'
          ? polygonRingFromLatLngs(parseCoordinates(g.Coordinates))
          : circleToPolygonRing(g.Longitude, g.Latitude, g.RadiusMeters || 0);
      if (!ring || ring.length < 4) return;
      const meta = getZoneTypeMeta(getZoneType(g));
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: { color: meta.color },
      });
    });
    return { type: 'FeatureCollection', features };
  }, [geofences]);

  /** Active zones last edited before a capture date — candidates for review. */
  const countStaleZones = (captureDateStr: string): number => {
    const active = geofences.filter((g) => g.is_active);
    if (!captureDateStr) return active.length;
    const capture = Date.parse(captureDateStr);
    if (Number.isNaN(capture)) return active.length;
    return active.filter((g) => {
      const edited = Date.parse(g.updated_at || g.created_at || '');
      return Number.isNaN(edited) || edited < capture;
    }).length;
  };

  // Live preview geometry: reprojected quad while untouched, else the rect.
  const previewQuad = useMemo((): LngLatQuad | null => {
    const n = parseFloat(formNorth),
      s = parseFloat(formSouth);
    const ea = parseFloat(formEast),
      w = parseFloat(formWest);
    if ([n, s, ea, w].some((v) => !Number.isFinite(v)) || n <= s || ea <= w) return null;
    if (!boundsEdited && detection?.corners?.length === 4) return detection.corners as LngLatQuad;
    return [
      [w, n],
      [ea, n],
      [ea, s],
      [w, s],
    ];
  }, [formNorth, formSouth, formEast, formWest, boundsEdited, detection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Aerial Overlays</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload drone survey imagery to overlay on the live map
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchOverlays}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-teal-600"
          >
            <FaSyncAlt size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
          <FaExclamationCircle size={14} className="flex-shrink-0" />
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <FaTimes size={12} />
          </button>
        </div>
      )}

      {/* Post-save nudge: fresh imagery → re-check the pile zones against it */}
      {zoneReview && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800 flex items-start gap-2">
          <FaCheckCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">“{zoneReview.name}” saved and live on the map.</p>
            <p className="text-xs mt-0.5 text-teal-700">
              {zoneReview.staleCount > 0
                ? `${zoneReview.staleCount} zone${zoneReview.staleCount === 1 ? ' was' : 's were'} last edited before this survey was captured — stockpiles move, so check the outlines still match the new imagery.`
                : 'Take a quick look at the live map to confirm the zone outlines still match the new imagery.'}
            </p>
            {onReviewZones && (
              <button
                type="button"
                onClick={() => {
                  setZoneReview(null);
                  onReviewZones();
                }}
                className="mt-2 flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-md"
              >
                <FaDrawPolygon size={11} /> Review zones on Live View
              </button>
            )}
          </div>
          <button onClick={() => setZoneReview(null)} className="text-teal-400 hover:text-teal-600">
            <FaTimes size={12} />
          </button>
        </div>
      )}

      {/* File picker and drop zone (geofencing.manage), always open (ST-00281) */}
      {canManage && (
        <section
          aria-labelledby="aerial-upload-title"
          className="bg-surface border border-gray-200 rounded-lg p-5"
        >
          <h3 id="aerial-upload-title" className="text-sm font-semibold text-gray-700 mb-4">
            Upload Aerial Imagery
          </h3>
          <div
            data-testid="aerial-drop-zone"
            data-dragging={dragging || undefined}
            onDragEnter={handleDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'
            }`}
          >
            <FaImage className="mx-auto text-gray-300 mb-3" size={36} />
            <p className="text-sm text-gray-600 mb-1">
              {dragging
                ? 'Drop the files to upload them'
                : 'Drop drone survey images here, or choose them — several files at once is fine'}
            </p>
            <p className="text-xs text-gray-400 mb-4">
              GeoTIFF recommended — position, capture date and footprint are detected automatically
              (SA Lo / UTM / lat-lon). PNG, JPG and WebP need manual bounds. Max {MAX_UPLOAD_MB}MB
              each. Uploads keep running if you navigate away.
            </p>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              accept=".tif,.tiff,.png,.jpg,.jpeg,.webp"
              onChange={handleFilesSelected}
              className="hidden"
              aria-label="Aerial image files"
              data-testid="aerial-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-lg"
            >
              <FaUpload size={12} /> Choose files
            </button>
          </div>
        </section>
      )}

      {/* Upload queue — lives across navigation */}
      {canManage && queue.items.length > 0 && (
        <div className="bg-surface border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Uploads</h3>
            <span className="text-[11px] text-gray-400">
              Uploads continue if you navigate away · unsaved uploads are kept for 48 hours
            </span>
          </div>
          <ul className="divide-y divide-gray-100">
            {queue.items.map((item) => (
              <li key={item.id} className="py-2.5 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{item.fileName}</p>
                  <p className="text-[11px] text-gray-400">{fmtMB(item.fileSize)}</p>
                </div>

                {item.status === 'queued' && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500">
                    <FaClock size={11} /> Queued
                  </span>
                )}

                {item.status === 'uploading' && (
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      {item.pct === 0 ? (
                        <div className="h-full w-1/2 bg-teal-400 rounded-full animate-pulse mx-auto" />
                      ) : (
                        <div
                          className="h-full bg-teal-500 rounded-full transition-all"
                          style={{ width: `${item.pct}%` }}
                        />
                      )}
                    </div>
                    <span className="text-xs text-gray-500 w-9 text-right">
                      {item.pct > 0 ? `${item.pct}%` : '…'}
                    </span>
                  </div>
                )}

                {item.status === 'processing' && (
                  <span className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-500" />
                    Processing…
                  </span>
                )}

                {item.status === 'detected' && (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <FaCheckCircle size={11} />{' '}
                      {item.detection?.boundsDetected ? 'Position detected' : 'Uploaded'}
                    </span>
                    <button
                      type="button"
                      onClick={() => openReview(item)}
                      className={`px-3 py-1 text-xs font-medium rounded-md ${
                        reviewId === item.id
                          ? 'text-teal-700 bg-teal-50 border border-teal-200'
                          : 'text-white bg-teal-500 hover:bg-teal-600'
                      }`}
                    >
                      {reviewId === item.id ? 'Reviewing below' : 'Review & Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => uploadQueue.dismiss(item.id)}
                      title="Discard this upload"
                      className="text-gray-300 hover:text-red-500"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                )}

                {item.status === 'failed' && (
                  <div className="flex items-center gap-2 max-w-[50%]">
                    <span className="text-xs text-red-600 truncate" title={item.error ?? undefined}>
                      {item.error}
                    </span>
                    {item.file && (
                      <button
                        type="button"
                        onClick={() => uploadQueue.retry(item.id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-600 border border-teal-300 rounded-md hover:bg-teal-50"
                      >
                        <FaSyncAlt size={10} /> Retry
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => uploadQueue.dismiss(item.id)}
                      className="text-gray-300 hover:text-red-500"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Review & Save */}
      {canManage && reviewItem && detection && (
        <div className="bg-surface border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 truncate">
              Review &amp; Save — {reviewItem.fileName}
            </h3>
            <button onClick={() => setReviewId(null)} className="text-gray-400 hover:text-gray-600">
              <FaTimes size={14} />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            {/* Detection result */}
            {boundsDetected ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <FaCheckCircle size={14} />
                  Position detected from GeoTIFF — check the alignment below, then save
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-green-700/90 pl-6">
                  {detection?.nativeCrs && (
                    <span>
                      <FaGlobeAfrica className="inline mr-1" size={10} />
                      {detection.nativeCrs}
                    </span>
                  )}
                  {fmtGsd(detection?.gsdMeters) && <span>{fmtGsd(detection.gsdMeters)}</span>}
                  {detection?.imageWidth && (
                    <span>
                      {detection.imageWidth}×{detection.imageHeight}px
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <div className="flex items-center gap-2 font-medium">
                  <FaExclamationCircle size={14} />
                  No usable georeferencing found — enter the bounds manually below
                </div>
                {detection?.warnings?.length > 0 && (
                  <ul className="text-xs mt-1 pl-6 list-disc list-inside">
                    {detection.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {boundsDetected &&
              detection?.warnings?.filter((w) => !w.startsWith('No ')).length > 0 && (
                <div className="text-xs text-gray-500 flex items-start gap-1.5">
                  <FaInfoCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{detection.warnings.join(' ')}</span>
                </div>
              )}

            {/* Map alignment preview (with image) or plain thumbnail */}
            {previewQuad && detection?.filePath ? (
              <div className="h-72 rounded-lg overflow-hidden border border-gray-200 relative">
                <LexiStarMap
                  key={detection.filePath}
                  initialViewState={{
                    bounds: [
                      [
                        Math.min(...previewQuad.map((c) => c[0])),
                        Math.min(...previewQuad.map((c) => c[1])),
                      ],
                      [
                        Math.max(...previewQuad.map((c) => c[0])),
                        Math.max(...previewQuad.map((c) => c[1])),
                      ],
                    ],
                    fitBoundsOptions: { padding: 40 },
                  }}
                  defaultBasemap="satellite"
                  basemapTogglePosition="bottom-left"
                  reuseMaps={false}
                >
                  <Source
                    id="aerial-preview"
                    type="image"
                    url={`${serverBase}${detection.filePath}`}
                    coordinates={previewQuad}
                  >
                    <Layer
                      id="aerial-preview-layer"
                      type="raster"
                      paint={{ 'raster-opacity': formOpacity, 'raster-fade-duration': 0 }}
                    />
                  </Source>
                  {zonesFC.features.length > 0 && (
                    <Source id="aerial-preview-zones" type="geojson" data={zonesFC}>
                      <Layer
                        id="aerial-preview-zones-fill"
                        type="fill"
                        paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.12 }}
                      />
                      <Layer
                        id="aerial-preview-zones-line"
                        type="line"
                        paint={{ 'line-color': ['get', 'color'], 'line-width': 2 }}
                      />
                    </Source>
                  )}
                </LexiStarMap>
                <div className="absolute top-2 left-2 bg-surface/90 backdrop-blur rounded px-2 py-1 text-[11px] text-gray-600 shadow">
                  {zonesFC.features.length > 0
                    ? 'Preview — check the aerial lines up, and whether the zone outlines still match the piles'
                    : 'Preview — the overlay should line up with the satellite imagery'}
                </div>
              </div>
            ) : (
              detection?.filePath && (
                <div className="h-40 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={`${serverBase}${detection.thumbPath || detection.filePath}`}
                    alt="Preview"
                    className="w-full h-full object-contain"
                    onError={hideBrokenImage}
                  />
                </div>
              )
            )}

            {/* Name + Capture date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Stockyard Survey July 2026"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Capture Date{' '}
                  {detection?.suggestedCaptureDate &&
                    formCaptureDate === detection.suggestedCaptureDate && (
                      <span className="text-green-600 font-normal">(from filename)</span>
                    )}
                </label>
                <input
                  type="date"
                  value={formCaptureDate}
                  onChange={(e) => setFormCaptureDate(e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* Bounds — collapsed to a summary when auto-detected */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Bounds{' '}
                {boundsDetected && !boundsEdited ? (
                  <span className="text-green-600 font-normal">
                    (auto-detected — edit only if the preview is misaligned)
                  </span>
                ) : boundsDetected ? (
                  <span className="text-amber-600 font-normal">(manually adjusted)</span>
                ) : (
                  <span className="text-amber-600 font-normal">
                    * Enter manually (WGS84 decimal degrees)
                  </span>
                )}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  {
                    label: 'North (lat)',
                    value: formNorth,
                    set: setFormNorth,
                    placeholder: '-26.0853',
                  },
                  {
                    label: 'South (lat)',
                    value: formSouth,
                    set: setFormSouth,
                    placeholder: '-26.0901',
                  },
                  {
                    label: 'East (lng)',
                    value: formEast,
                    set: setFormEast,
                    placeholder: '29.9588',
                  },
                  {
                    label: 'West (lng)',
                    value: formWest,
                    set: setFormWest,
                    placeholder: '29.9548',
                  },
                ].map((f) => (
                  <div key={f.label}>
                    <label className="block text-[10px] text-gray-400 mb-0.5">{f.label}</label>
                    <input
                      type="number"
                      step="any"
                      value={f.value}
                      onChange={setBoundsField(f.set)}
                      placeholder={f.placeholder}
                      className={`w-full px-3 py-1.5 text-sm border rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 ${
                        boundsDetected && !boundsEdited
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Opacity */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Overlay Opacity: {Math.round(formOpacity * 100)}%
              </label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={formOpacity}
                onChange={(e) => setFormOpacity(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => {
                  uploadQueue.dismiss(reviewItem.id);
                  setReviewId(null);
                }}
                className="text-sm text-red-400 hover:text-red-600"
              >
                Discard Upload
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setReviewId(null)}
                  className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Back to Uploads
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 disabled:opacity-50 rounded-lg"
                >
                  {saving ? 'Saving...' : 'Save Overlay'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Overlay Cards */}
      {overlays.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          <FaImage className="mx-auto text-3xl text-gray-300 mb-2" />
          <p className="font-medium">No aerial overlays uploaded</p>
          <p className="text-sm mt-1">
            Upload drone or satellite imagery to overlay on the live map
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {overlays.map((ov) => (
            <div
              key={ov.overlay_id}
              data-testid={`overlay-card-${ov.overlay_id}`}
              className={`bg-surface border rounded-lg overflow-hidden transition-opacity ${
                ov.is_active ? 'border-teal-200' : 'border-gray-200 opacity-60'
              }`}
            >
              <div className="h-40 bg-gray-100 relative">
                <img
                  src={`${serverBase}${ov.thumb_path || ov.file_path}`}
                  alt={ov.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={hideBrokenImage}
                />
                {!ov.is_active && (
                  <div className="absolute inset-0 bg-gray-900 dark:bg-black bg-opacity-30 flex items-center justify-center">
                    <span className="text-white dark:text-gray-50 text-xs font-medium bg-gray-700 bg-opacity-80 px-2 py-1 rounded">
                      Inactive
                    </span>
                  </div>
                )}
              </div>
              <div className="p-3">
                <h4 className="font-semibold text-gray-900 text-sm truncate">{ov.name}</h4>
                {ov.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{ov.description}</p>
                )}
                <div className="mt-2 space-y-1 text-xs text-gray-500">
                  {ov.capture_date && (
                    <div className="flex items-center gap-1">
                      <FaCalendarAlt size={10} />
                      <span>Captured {new Date(ov.capture_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  <p>
                    Opacity: {Math.round((ov.opacity || 0.8) * 100)}%
                    {fmtGsd(ov.gsd_m) && <span> · {fmtGsd(ov.gsd_m)}</span>}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                  {canManage ? (
                    <button
                      onClick={() => handleToggleActive(ov)}
                      title={ov.is_active ? 'Hide from the live map' : 'Show on the live map'}
                      className={`flex items-center gap-1 text-xs font-medium ${
                        ov.is_active
                          ? 'text-teal-600 hover:text-teal-700'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {ov.is_active ? <FaToggleOn size={16} /> : <FaToggleOff size={16} />}
                      {ov.is_active ? 'Active' : 'Inactive'}
                    </button>
                  ) : (
                    <span
                      className={`text-xs font-medium ${ov.is_active ? 'text-teal-600' : 'text-gray-400'}`}
                    >
                      {ov.is_active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {canManage && (
                    <button
                      onClick={() => handleDelete(ov.overlay_id)}
                      className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600"
                    >
                      <FaTrash size={11} /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
